/**
 * Relief Valve — opens at set pressure, closes below.
 *
 * Physics:
 *   P > P_set: valve opens, K = K_open, flow evacuates to sink
 *   P < P_set × (1 - blowdown): valve closes, K = K_closed
 *   Between: valve stays in current state (hysteresis)
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';

export class NSReliefValve implements NSComponent {
  id: string;
  type = 'reliefValve';
  params: Record<string, any>;
  private isOpen = false;

  constructor(id: string, params: {
    P_set?: number;         // Pa — opening pressure (default 10 MPa)
    blowdown?: number;      // fraction — pressure drop before reclose (default 0.05 = 5%)
    K_open?: number;        // Loss coefficient when open (default 1.0)
    capacity?: number;      // kg/s — rated mass flow at P_set (default 50)
  }) {
    this.id = id;
    this.params = { P_set: 10e6, blowdown: 0.05, K_open: 1.0, capacity: 50, ...params };
  }

  applyBoundaryConditions(inlet: Junction, outlet: Junction, _dt: number): ComponentUpdate {
    const { P_set, blowdown, capacity } = this.params;
    const P_back = this.params.P_back ?? 101325; // Pa — backpressure (default atmospheric)

    // Hysteresis logic
    const P_close = P_set * (1 - blowdown);
    if (inlet.P > P_set) this.isOpen = true;
    if (inlet.P < P_close) this.isOpen = false;

    if (this.isOpen) {
      // Linear opening ramp: 0→100% over 10% accumulation above P_set
      const overP = Math.max(0, inlet.P - P_set);
      const fraction = Math.min(1, overP / (P_set * 0.1));
      // Backpressure correction: if outlet is pressurized, reduce relief flow.
      // At atmospheric back-pressure, factor ≈ 1. At P_back = P_inlet, factor = 0.
      const bp_factor = inlet.P > P_back ? Math.sqrt((inlet.P - P_back) / inlet.P) : 0;
      const mdot_relief = capacity * fraction * bp_factor;
      return {
        deltaP: 0,
        deltaH: 0,
        mdot: mdot_relief,
        momentumSource: 0,
        energySource: 0,
        metadata: { isOpen: 1, mdot_relief, P_set, dP: overP },
      };
    }

    return { deltaP: 0, deltaH: 0, momentumSource: 0, energySource: 0, metadata: { isOpen: 0, P_set, dP: 0 } };
  }
}
